import { isFeatureEnabled } from '@lib';
import { useStore } from '@stores';

/**
 * What a Technician may do in booking and order flows. `true` = same as a manager, `false` = hidden
 * or disabled for technicians only. Managers are never affected by these flags.
 *
 * The app has two kinds of user, a manager and a technician: everyone who is not a technician has
 * all of it.
 */
const TECHNICIAN_ACCESS = {
  header: {
    /** The New Booking button, here and in the calendar toolbar. */
    newBooking: false,
    /** The QuickPay button, here and in the calendar toolbar. */
    quickPay: false,
  },
  calendar: {
    /** Drawing a slot on the calendar to start a booking. */
    createBooking: false,
    /** The side panel of unscheduled / unassigned jobs. */
    sidePanel: false,
    /** Dragging cards, and dropping panel cards onto the calendar. */
    dragDrop: true,
    /** Technician / Workstation / Unavailability toolbar buttons (and the phone's equivalents). */
    resourceViews: true,
    /** Unavailability: the toolbar button and "Manage availability" in the column menu. */
    unavailability: false,
    /** The Lookup: a search over the calendar's jobs, any customer and any day. Technicians have it too. */
    lookup: true,
    /** Per-column menu: print the daily job list, add time off. */
    resourceMenu: true,
    /** In the Technician view: every technician and job, not only their own. */
    seeAllJobs: false,
    /** In the Workstation view: every job on the workstations, not only their own. */
    seeAllWorkStationJobs: true,
  },
  order: {
    /** The order page's header cards (job, customer, invoice summaries). */
    headerCards: true,
    /** Editing a service's price, discount, notes. When false they get the read-only card. */
    editServiceDetails: true,
    /** The invoice card on the order page (a panel of its own on the phone). */
    invoiceCard: false,
    /** The Discount field on a service and on a service extra (the discount still applies). */
    discount: false,
    /** Sub Total, VAT and total: on a service, on a service extra, and in the footers. */
    totals: false,
    /** The Cost / Margin / Sale fields of a service extra. */
    extraPricing: false,
    /** Editing a service extra (part). When false they get the read-only form. */
    editExtras: true,
    /** Upload parts / Return parts. */
    partsActions: false,
    /** Raising the invoice once the job is complete. */
    generateInvoice: false,
    /** Editing other people's work logs and choosing the technician on one; without it, only their own. */
    manageWorkLogs: false,
    /** The reminders step after saving a service history entry. */
    serviceHistoryReminders: true,
    /** The Unassign button in the order drawer footer. */
    unassign: false,
    /** The Details button on the customer card. */
    customerDetails: false,
    /** The Edit (or Assign) button on the customer card. */
    customerEdit: false,
    /** "Create" in Related orders. */
    createRelated: true,
    /** Vehicle reminder fields. */
    editReminders: true,
    /** The Drop-off row on the job card, and the warning when it drifts from the job date. */
    dropOffRow: false,
    /** The drop-off date edit button in the order header. */
    editDropOff: true,
  },
};

export const useAccess = () => {
  const { authenticationStore } = useStore();
  const role = authenticationStore.currentUser?.role;

  const isTechnician = role === 'Technician';
  // One rule for everything in the table: only a technician is held to its flag.
  const allow = (flag: boolean) => !isTechnician || flag;
  const resolve = <T extends Record<string, boolean>>(flags: T) =>
    Object.fromEntries(Object.entries(flags).map(([key, flag]) => [key, allow(flag)])) as Record<
      keyof T,
      boolean
    >;

  return {
    isTechnician,
    features: {
      builds: isFeatureEnabled('App.Builds'),
    },
    header: resolve(TECHNICIAN_ACCESS.header),
    calendar: resolve(TECHNICIAN_ACCESS.calendar),
    order: resolve(TECHNICIAN_ACCESS.order),
  };
};
